import { useState, useEffect, useCallback } from 'react'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { Enums, EnumItem } from '../interfaces'
import { TaskType, ButtonGroup } from '../types'
import { TASK_TYPES } from '../constants'

interface UseTasksEnumsProps {
  taskType: TaskType
  isLeader: boolean
  onSuccess?: (response: any) => void
  onError?: () => void
}

export const useTasksEnums = ({ taskType, isLeader, onError }: UseTasksEnumsProps) => {
  const [enums, setEnums] = useState<Enums>({})
  const [loading, setLoading] = useState<boolean>(true) // По умолчанию true для первой загрузки
  const [error, setError] = useState<string | null>(null)
  const [montageTypes, setMontageTypes] = useState<number[]>([])
  const [measurementTypes, setMeasurementTypes] = useState<number[]>([])
  const [deliveryTypes, setDeliveryTypes] = useState<number[]>([])
  const [saleTypes, setSaleTypes] = useState<number[]>([])
  const [claimTypes, setClaimTypes] = useState<number[]>([])

  const convertEnumObject = useCallback((data: Record<string, any>): Record<string, EnumItem[]> => {
    const convertedData: Record<string, EnumItem[]> = {}
    Object.keys(data).forEach(key => {
      const value = data[key]
      if (Array.isArray(value)) {
        convertedData[key] = value.map(
          (item: { value: string; id: string; disabled?: boolean }) => ({
            label: item.value,
            value: item.id,
            disabled: item?.disabled || false,
          }),
        )
      } else if (typeof value === 'object' && value !== null) {
        convertedData[key] = Object.keys(value).map(id => ({
          label: value[id],
          value: id,
        }))
      }
    })

    return convertedData
  }, [])

  const processButtonGroups = useCallback((enumsData: any): ButtonGroup => {
    const buttonsGroup = {
      montage:
        enumsData?.task_type?.filter(item => item.value === TASK_TYPES.MONTAGE).map(i => i.id) ||
        [],
      measurement:
        enumsData?.task_type
          ?.filter(item => item.value === TASK_TYPES.MEASUREMENT)
          .map(i => i.id) || [],
      delivery:
        enumsData?.task_type?.filter(item => item.value === TASK_TYPES.DELIVERY).map(i => i.id) ||
        [],
    }

    setMontageTypes(buttonsGroup.montage)
    setMeasurementTypes(buttonsGroup.measurement)
    setDeliveryTypes(buttonsGroup.delivery)

    setSaleTypes(
      enumsData?.task_type
        ?.filter(
          item =>
            ![
              ...buttonsGroup.montage,
              ...buttonsGroup.measurement,
              ...buttonsGroup.delivery,
            ].includes(item.id),
        )
        .map(i => i.id) || [],
    )

    return buttonsGroup
  }, [])

  useEffect(() => {
    let isCancelled = false

    const loadEnums = async () => {
      setLoading(true)

      try {
        // Загружаем enums
        const [enumsData] = await Promise.all([fetchAPI(`/api/v2/task/${taskType}/enum`)])

        if (isCancelled) return

        // Создаем копию для работы с данными (избегаем мутаций)
        const processedEnums = { ...enumsData }

        if (taskType === 'case') {
          processButtonGroups(processedEnums)
        }

        if (isLeader) {
          const [enumsHr, enumsClaim] = await Promise.all([
            fetchAPI(`/api/v2/task/hr/enum`),
            fetchAPI(`/api/v2/task/claim/enum`),
          ])

          if (isCancelled) return

          if ('task_type' in processedEnums && processedEnums.task_type) {
            processedEnums.task_type = [
              { id: 0, value: 'Продажи', disabled: true },
              ...processedEnums.task_type,
              { id: 0, value: 'HR', disabled: true },
              ...(enumsHr?.task_type || []),
              { id: 0, value: 'Рекламации', disabled: true },
              ...(enumsClaim?.task_type || []),
            ]
          }

          const responsibleMap = new Map()
          if (processedEnums?.responsible) {
            processedEnums.responsible.forEach(item => responsibleMap.set(item.id, item))
          }
          if (enumsHr?.responsible) {
            enumsHr.responsible.forEach(item => responsibleMap.set(item.id, item))
          }
          if (enumsClaim?.responsible) {
            enumsClaim.responsible.forEach(item => responsibleMap.set(item.id, item))
          }
          processedEnums.responsible = Array.from(responsibleMap.values())

          if (enumsHr?.stages && !processedEnums.stages) {
            processedEnums.stages = enumsHr.stages
          }
          if (enumsClaim?.stages) {
            processedEnums.stages = enumsClaim.stages
          }

          if (enumsClaim?.task_type) {
            setClaimTypes(enumsClaim.task_type.map(i => i.id))
          }
        }

        setEnums(convertEnumObject(processedEnums))
        setError(null)
      } catch (e) {
        const errorMessage = (e as any)?.message || 'Ошибка загрузки справочников'
        setError(errorMessage)
        console.error('Ошибка загрузки enums:', e)
        onError?.()
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }

    loadEnums()

    return () => {
      isCancelled = true
    }
  }, [taskType, isLeader, convertEnumObject, processButtonGroups, onError])

  return {
    enums,
    loading,
    error,
    montageTypes,
    measurementTypes,
    deliveryTypes,
    saleTypes,
    claimTypes,
  }
}
